import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOVES = [
    { key: "squat", label: "Squat" },
    { key: "lunge", label: "Lunge" },
    { key: "hinge", label: "Hinge" },
    { key: "push", label: "Push" },
    { key: "pull", label: "Pull" },
    { key: "core", label: "Core" },
];

const ADULT_TRACKS = new Set(["pursuing_pro", "athletic_aesthetic"]);

export default function AssessmentFormModal({ open, athlete, currentStatus, onClose, onSaved }) {
    const isAdult = athlete && ADULT_TRACKS.has(athlete.track);
    const [includeSpeed, setIncludeSpeed] = useState(!isAdult);
    const [shredmillSpeed, setShredmillSpeed] = useState("");
    const [shredmillResistance, setShredmillResistance] = useState("");
    const [tenYd, setTenYd] = useState("");
    const [twentyYd, setTwentyYd] = useState("");
    const [approachJump, setApproachJump] = useState("");
    const [verticalJump, setVerticalJump] = useState("");
    const [movement, setMovement] = useState({ squat: "", lunge: "", hinge: "", push: "", pull: "", core: "" });
    const [overallNotes, setOverallNotes] = useState("");
    const [newStatus, setNewStatus] = useState("member");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    // Hydrate any existing assessment when opened.
    useEffect(() => {
        if (!open || !athlete) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/coach/assessments/${athlete.athlete_id}`, { withCredentials: true });
                if (cancelled) return;
                const d = r.data || {};
                setIncludeSpeed(Boolean(d.include_speed ?? !isAdult));
                const s = d.speed || {};
                setShredmillSpeed(s.shredmill_speed ?? "");
                setShredmillResistance(s.shredmill_resistance ?? "");
                setTenYd(s.ten_yd ?? "");
                setTwentyYd(s.twenty_yd ?? "");
                setApproachJump(s.approach_jump ?? "");
                setVerticalJump(s.vertical_jump ?? "");
                const m = d.movement || {};
                setMovement({
                    squat: m.squat || "", lunge: m.lunge || "", hinge: m.hinge || "",
                    push: m.push || "", pull: m.pull || "", core: m.core || "",
                });
                setOverallNotes(d.overall_notes || "");
            } catch (e) {
                if (e?.response?.status !== 404) {
                    toast.error("Couldn't load existing assessment", { description: e?.response?.data?.detail });
                }
                // 404 = no prior assessment, leave defaults
                setIncludeSpeed(!isAdult);
                setShredmillSpeed(""); setShredmillResistance("");
                setTenYd(""); setTwentyYd(""); setApproachJump(""); setVerticalJump("");
                setMovement({ squat: "", lunge: "", hinge: "", push: "", pull: "", core: "" });
                setOverallNotes("");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, athlete, isAdult]);

    if (!open || !athlete) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const toNum = (v) => v === "" || v === null || v === undefined ? null : Number(v);
            await axios.post(`${API}/coach/assessments`, {
                athlete_id: athlete.athlete_id,
                include_speed: includeSpeed,
                shredmill_speed: includeSpeed ? toNum(shredmillSpeed) : null,
                shredmill_resistance: includeSpeed ? toNum(shredmillResistance) : null,
                ten_yd: includeSpeed ? toNum(tenYd) : null,
                twenty_yd: includeSpeed ? toNum(twentyYd) : null,
                approach_jump: includeSpeed ? toNum(approachJump) : null,
                vertical_jump: includeSpeed ? toNum(verticalJump) : null,
                movement,
                overall_notes: overallNotes,
                new_status: newStatus || null,
            }, { withCredentials: true });
            toast.success("Assessment saved");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="assessment-form-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <ClipboardList className="w-5 h-5 text-brand-red shrink-0" />
                        <h3 className="text-lg font-semibold truncate">
                            Assessment — {athlete.first_name} {athlete.last_name}
                        </h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
                    {loading && <div className="text-xs text-zinc-300">Loading prior data…</div>}

                    {/* Speed/jump section toggle */}
                    <div className="flex items-center justify-between border border-zinc-800 rounded-lg p-3">
                        <div>
                            <div className="text-sm font-medium">Include speed & jump tests</div>
                            <div className="text-xs text-zinc-300">{isAdult ? "Optional for adults" : "Standard for youth athletes"}</div>
                        </div>
                        <button
                            type="button"
                            data-testid="toggle-include-speed"
                            onClick={() => setIncludeSpeed((v) => !v)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeSpeed ? "bg-brand-red" : "bg-zinc-700"}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeSpeed ? "translate-x-6" : "translate-x-1"}`} />
                        </button>
                    </div>

                    {includeSpeed && (
                        <div>
                            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-3">Speed & Power</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Shredmill speed">
                                    <NumInput value={shredmillSpeed} onChange={setShredmillSpeed} testid="metric-shredmill-speed" />
                                </Field>
                                <Field label="Shredmill resistance">
                                    <NumInput value={shredmillResistance} onChange={setShredmillResistance} testid="metric-shredmill-resistance" />
                                </Field>
                                <Field label="10-yard dash (sec)">
                                    <NumInput value={tenYd} onChange={setTenYd} testid="metric-10yd" step="0.01" />
                                </Field>
                                <Field label="20-yard dash (sec)">
                                    <NumInput value={twentyYd} onChange={setTwentyYd} testid="metric-20yd" step="0.01" />
                                </Field>
                                <Field label="Approach jump (in)">
                                    <NumInput value={approachJump} onChange={setApproachJump} testid="metric-approach-jump" step="0.5" />
                                </Field>
                                <Field label="Vertical jump (in)">
                                    <NumInput value={verticalJump} onChange={setVerticalJump} testid="metric-vertical-jump" step="0.5" />
                                </Field>
                            </div>
                        </div>
                    )}

                    {/* Movement screen (notes-only) */}
                    <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-3">Movement screen</div>
                        <p className="text-[11px] text-zinc-300 mb-3">Free-text notes for each pattern. Blank notes are skipped on outputs.</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {MOVES.map((m) => (
                                <Field key={m.key} label={m.label}>
                                    <textarea
                                        rows={2}
                                        data-testid={`movement-${m.key}`}
                                        value={movement[m.key]}
                                        onChange={(e) => setMovement({ ...movement, [m.key]: e.target.value })}
                                        placeholder="Notes…"
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                                    />
                                </Field>
                            ))}
                        </div>
                    </div>

                    {/* Overall notes */}
                    <Field label="Overall notes">
                        <textarea
                            rows={3}
                            data-testid="metric-overall-notes"
                            value={overallNotes}
                            onChange={(e) => setOverallNotes(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                        />
                    </Field>

                    {/* Status flip */}
                    <div className="border border-zinc-800 rounded-lg p-4 space-y-3">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">After saving, set status to</div>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { v: "member", l: "Member" },
                                { v: "inactive", l: "Inactive" },
                                { v: "", l: `Keep ${currentStatus || "—"}` },
                            ].map((opt) => (
                                <button
                                    key={opt.v || "keep"}
                                    type="button"
                                    data-testid={`status-after-${opt.v || "keep"}`}
                                    onClick={() => setNewStatus(opt.v)}
                                    className={`py-2 border text-[11px] tracking-wider uppercase transition ${
                                        newStatus === opt.v
                                            ? "border-brand-red text-brand-red bg-brand-red/10"
                                            : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                    }`}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-2 shrink-0 bg-zinc-950 rounded-b-2xl">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical" disabled={saving}>Cancel</button>
                    <button
                        type="button"
                        data-testid="assessment-save"
                        onClick={submit}
                        disabled={saving}
                        className="btn-tactical"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {saving ? "Saving…" : "Save assessment"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">{label}</span>
        <div className="mt-1.5">{children}</div>
    </label>
);

function NumInput({ value, onChange, testid, step = "0.1" }) {
    return (
        <input
            type="number"
            data-testid={testid}
            step={step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
        />
    );
}
